import React from 'react';
import { useTranslation } from 'react-i18next';
import { DotsThreeIcon, LinkBreakIcon, TrashIcon } from '@phosphor-icons/react';
import { Dropdown } from '@origo/origo-ui';
import type { DocumentItem } from '../../../types';
import { FILE_ICONS } from './fileIcons';
import styles from './DocumentList.module.css';

export type DocumentAction = 'delete' | 'unlink';

interface DocumentListProps {
    items: DocumentItem[];
    onOpen: (id: string) => void;
    onAction: (action: DocumentAction, id: string) => void;
    /** Radhandlingen i menyen: slett dokumentet, eller kun fjern det fra behandlingen. */
    rowAction?: DocumentAction;
}

const DocumentList: React.FC<DocumentListProps> = ({ items, onOpen, onAction, rowAction = 'delete' }) => {
    const { t } = useTranslation();
    const unlink = rowAction === 'unlink';

    return (
        <ul className={styles.list}>
            {items.map(item => {
                const meta = [item.dokumenttype, item.dato, item.saksnummer].filter(Boolean);
                const Icon = FILE_ICONS[item.filtype];
                return (
                    <li key={item.id}>
                        <div className={styles.row}>
                            <button
                                type="button"
                                className={styles.rowMain}
                                onClick={() => onOpen(item.id)}
                                aria-label={item.dokumentnavn}
                            >
                                <span className={styles.icon} aria-hidden="true">
                                    {/* 32px så PDF/DOC-bokstavene i glyfen er lesbare. */}
                                    <Icon size={32} weight="duotone" />
                                </span>
                                <span className={styles.text}>
                                    <span className={styles.title}>{item.dokumentnavn}</span>
                                    {meta.length > 0 && (
                                        <span className={styles.meta}>
                                            {meta.map((value, i) => (
                                                <React.Fragment key={i}>
                                                    {i > 0 && (
                                                        <span className={styles.metaSeparator} aria-hidden="true">
                                                            ·
                                                        </span>
                                                    )}
                                                    <span className={styles.metaItem}>{value}</span>
                                                </React.Fragment>
                                            ))}
                                        </span>
                                    )}
                                    {item.beskrivelse && (
                                        <span className={styles.description}>{item.beskrivelse}</span>
                                    )}
                                </span>
                            </button>
                            <Dropdown
                                align="end"
                                className={styles.menu}
                                trigger={
                                    <span aria-label={t('documents.moreActions')}>
                                        <DotsThreeIcon size={16} weight="bold" />
                                    </span>
                                }
                                groups={[
                                    {
                                        items: [
                                            {
                                                id: rowAction,
                                                label: t(unlink ? 'documents.actions.unlink' : 'documents.actions.delete'),
                                                icon: unlink ? (
                                                    <LinkBreakIcon size={16} weight="duotone" />
                                                ) : (
                                                    <TrashIcon size={16} weight="duotone" />
                                                ),
                                                danger: !unlink,
                                                onClick: () => onAction(rowAction, item.id),
                                            },
                                        ],
                                    },
                                ]}
                            />
                        </div>
                    </li>
                );
            })}
        </ul>
    );
};

export default DocumentList;
